import React from "react";
import { Link } from "react-router-dom";
import image15 from "../image/15.jpg";
import image14 from "../image/14.jpg";

function AboutUs() {
  return (
    <div className="w-full bg-slate-50/50 font-sans overflow-hidden">

      {/* Premium Hero Section */}
      <div className="relative w-full bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 pt-20 pb-28 md:pt-24 md:pb-36 lg:pt-28 lg:pb-40 px-6 overflow-hidden">

        {/* Hero Background Image with Tint */}
        <img
          src={image14}
          alt="PoloPipes Manufacturing Operations"
          className="w-full h-full absolute inset-0 object-cover mix-blend-overlay opacity-20 pointer-events-none"
        />

        {/* Ambient Radial Glow Effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/10 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-4xl mx-auto text-center text-white space-y-4 relative z-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-2 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
            <span className="text-xs font-bold uppercase tracking-wider text-orange-50">Company Profile</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight drop-shadow-md">
            About PoloPipes
          </h1>
          <p className="text-sm sm:text-base md:text-lg font-normal leading-relaxed text-orange-50 opacity-95 max-w-2xl mx-auto">
            Delivering an extensive range of innovative, BIS-certified polymer piping systems engineered to empower fluid management across municipal, agricultural, and industrial infrastructure.
          </p>
        </div>

        {/* Clean Solid White Curved SVG Divider */}
        <div className="absolute bottom-0 left-0 right-0 w-full overflow-hidden leading-[0] z-10">
          <svg
            className="relative block w-full h-[60px] md:h-[100px]"
            viewBox="0 0 1920 120"
            preserveAspectRatio="none"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0,120 C640,30 1280,30 1920,120 L1920,120 L0,120 Z"
              fill="#ffffff"
            />
          </svg>
        </div>
      </div>

      {/* Breadcrumb Navigation Bar */}
      <div className="bg-white py-3.5 px-6 sm:px-12 border-b border-slate-100 relative z-20">
        <div className="max-w-7xl mx-auto flex items-center space-x-2 text-xs sm:text-sm font-medium text-slate-500">
          <Link to="/" className="text-orange-600 hover:text-orange-700 font-semibold transition-colors">Home</Link>
          <span className="text-slate-300 font-light">&gt;</span>
          <span className="text-slate-800">About Us</span>
        </div>
      </div>

      {/* Brand Hashtag Introduction */}
      <section className="py-16 px-6 text-center max-w-4xl mx-auto space-y-4">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-orange-100 text-orange-700">
          Who We Are
        </span>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
          #wearePoloPipes
        </h2>
        <div className="text-slate-600 text-xs sm:text-base leading-relaxed space-y-3 font-normal max-w-3xl mx-auto">
          <p className="text-slate-900 font-bold text-base sm:text-lg">
            Experts in sustainable water network solutions.
          </p>
          <p>
            We are leading the way in revolutionizing the infrastructure landscape by manufacturing cutting-edge polymer piping assets that empower sustainable regional development. We aspire to connect communities, agriculture, and industries through safe, high-endurance fluid transport systems.
          </p>
        </div>
      </section>

      {/* Corporate Dedication Message & Visual Frame */}
      <section className="bg-white py-16 px-6 relative border-y border-slate-100">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-center gap-12">

          {/* Card Message */}
          <div className="w-full lg:w-1/2">
            <div className="bg-slate-900 rounded-3xl p-8 md:p-10 shadow-xl text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>

              <span className="text-orange-400 text-xs font-bold uppercase tracking-wider mb-2 block">
                Our Commitment
              </span>
              <h3 className="text-2xl sm:text-3xl font-black mb-4 tracking-tight">
                Fluid Network Assurance
              </h3>
              <p className="text-sm sm:text-base leading-relaxed font-normal text-slate-300 italic border-l-2 border-orange-500 pl-4">
                "Fluids are in constant motion. To harness them effectively, precision engineering and uncompromising material standards are essential. From homes and civic projects to industrial facilities and farms, fluids play a vital role. Our mission is to provide total peace of mind by delivering robust piping assets built to endure for generations."
              </p>
            </div>
          </div>

          {/* Image Frame */}
          <div className="w-full lg:w-1/2">
            <div className="overflow-hidden rounded-3xl shadow-xl border border-slate-200/80 bg-slate-100">
              <img
                src={image15}
                alt="PoloPipes Manufacturing Operations"
                className="w-full h-[320px] sm:h-[400px] object-cover hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
            </div>
          </div>

        </div>
      </section>

      {/* Grid Profile Overview Section */}
      <section className="py-20 px-6 max-w-6xl mx-auto space-y-12">
        <div className="text-center space-y-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wider uppercase bg-orange-100 text-orange-700">
            Performance Metrics
          </span>
          <h3 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            PoloPipes at a Glance
          </h3>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          {/* Feature 1 */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200/80 shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-300 flex flex-col items-center text-center space-y-3">
            <div className="p-3.5 rounded-2xl bg-orange-500 text-white shadow-md shadow-orange-500/20">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
            <h4 className="text-base font-bold text-slate-900">Highly Experienced Team</h4>
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">Engineers and technical personnel bringing deep domain expertise accumulated over decades of manufacturing excellence.</p>
          </div>

          {/* Feature 2 */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200/80 shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-300 flex flex-col items-center text-center space-y-3">
            <div className="p-3.5 rounded-2xl bg-sky-500 text-white shadow-md shadow-sky-500/20">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </div>
            <h4 className="text-base font-bold text-slate-900">Modern Infrastructure</h4>
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">State-of-the-art extrusion lines equipped with automatic process controls for uniform wall thickness and density.</p>
          </div>

          {/* Feature 3 */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200/80 shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-300 flex flex-col items-center text-center space-y-3">
            <div className="p-3.5 rounded-2xl bg-emerald-500 text-white shadow-md shadow-emerald-500/20">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
              </svg>
            </div>
            <h4 className="text-base font-bold text-slate-900">High-Grade Materials</h4>
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">Mandatory testing of 100% incoming virgin resin to ensure pressure endurance, chemical resistance, and longevity.</p>
          </div>

          {/* Feature 4 */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200/80 shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-300 flex flex-col items-center text-center space-y-3">
            <div className="p-3.5 rounded-2xl bg-indigo-500 text-white shadow-md shadow-indigo-500/20">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <h4 className="text-base font-bold text-slate-900">Robust Supply Chain</h4>
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">Well-organized logistics network ensuring prompt site dispatch for civic, agricultural, and commercial buyers.</p>
          </div>

          {/* Feature 5 */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200/80 shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-300 flex flex-col items-center text-center space-y-3">
            <div className="p-3.5 rounded-2xl bg-purple-500 text-white shadow-md shadow-purple-500/20">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h4 className="text-base font-bold text-slate-900">Competitive Value</h4>
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">Cost-optimized production processes designed to offer top-tier certified products at competitive rates.</p>
          </div>

          {/* Feature 6 */}
          <div className="p-8 rounded-3xl bg-white border border-slate-200/80 shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-300 flex flex-col items-center text-center space-y-3">
            <div className="p-3.5 rounded-2xl bg-amber-500 text-white shadow-md shadow-amber-500/20">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h4 className="text-base font-bold text-slate-900">BIS Accredited</h4>
            <p className="text-xs sm:text-sm text-slate-500 leading-relaxed">Full compliance with Indian Standards specifications (IS 4985, IS 4984, IS 12818, IS 10124, IS 14182) and ISO certification.</p>
          </div>

        </div>
      </section>

    </div>
  );
}

export default AboutUs;